import { createAsyncThunk } from "@reduxjs/toolkit"

import { errorSave } from "../../../redux/errors.slice"
import { type RootState } from "../../../store.ts"
import { type BasePagination } from "../../../types/api.ts"
import { showResponseMessage } from "../../../utils/handlers/show-response-message.ts"
import { mapDeletionErrorDetails } from "../api/map-deletion-error.ts"
import * as services from "../api/services.ts"
import type {
  ApproveDeletionParams,
  DeletionRequestFilters,
  RejectDeletionParams,
  RetryDeletionParams
} from "../api/services.type.ts"
import type { AccountDeletionRequest } from "../types/types.ts"
import {
  RETRY_POLL_ATTEMPTS,
  RETRY_POLL_INTERVAL_MS,
  accountDeletionSliceName
} from "./account-deletion.constants.ts"

/**
 * The envelope renderer stamps every POST with a generic "Created
 * successfully", which `showResponseMessage` would prefer over ours. Passing an
 * empty response drops it back to our message — the idiom `contracts.thunks`
 * already uses.
 */
const showSuccess = (message: string): void => showResponseMessage({}, message)

const wait = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms))

export const fetchDeletionRequests = createAsyncThunk<
  BasePagination<AccountDeletionRequest>,
  DeletionRequestFilters & { isMore?: boolean }
>(
  `${accountDeletionSliceName}/fetchDeletionRequests`,
  // `isMore` tells the reducer whether to append; it is not a query parameter.
  async ({ isMore: _isMore, ...filters }, { dispatch, rejectWithValue }) => {
    const response = await services.getDeletionRequests(filters)
    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(
        response.data || { message: "Failed to fetch deletion requests" }
      )
    }
    return response.data
  }
)

export const fetchPendingDeletionCount = createAsyncThunk<number>(
  `${accountDeletionSliceName}/fetchPendingCount`,
  async (_, { rejectWithValue }) => {
    const response = await services.getDeletionRequests({
      status: "pending",
      page: 1,
      page_size: 1
    })
    if (!response.ok) {
      return rejectWithValue(response.data)
    }
    return (response.data?.count as number) ?? 0
  }
)

export const approveDeletionRequestThunk = createAsyncThunk<
  void,
  ApproveDeletionParams & { successMessage: string },
  { state: RootState }
>(
  `${accountDeletionSliceName}/approve`,
  async (
    { successMessage, ...params },
    { dispatch, getState, rejectWithValue }
  ) => {
    const response = await services.approveDeletionRequest(params)
    if (!response.ok) {
      dispatch(errorSave(mapDeletionErrorDetails(response)))
      // The row is stale whenever the decision lost a race (409), so refresh it
      // instead of leaving actionable buttons on a request already decided.
      await dispatch(fetchDeletionRequests(getState().accountDeletion.filters))
      return rejectWithValue(
        response.data || { message: "Failed to approve deletion request" }
      )
    }

    const filters = getState().accountDeletion.filters
    await dispatch(fetchDeletionRequests(filters))
    showSuccess(successMessage)
  }
)

export const rejectDeletionRequestThunk = createAsyncThunk<
  void,
  RejectDeletionParams & { successMessage: string },
  { state: RootState }
>(
  `${accountDeletionSliceName}/reject`,
  async (
    { successMessage, ...params },
    { dispatch, getState, rejectWithValue }
  ) => {
    const response = await services.rejectDeletionRequest(params)
    if (!response.ok) {
      dispatch(errorSave(mapDeletionErrorDetails(response)))
      await dispatch(fetchDeletionRequests(getState().accountDeletion.filters))
      return rejectWithValue(
        response.data || { message: "Failed to reject deletion request" }
      )
    }

    const filters = getState().accountDeletion.filters
    await dispatch(fetchDeletionRequests(filters))
    showSuccess(successMessage)
  }
)

/** Re-runs anonymization for a request stuck in `processing` (finalize failed). */
export const retryDeletionRequestThunk = createAsyncThunk<
  void,
  RetryDeletionParams & { successMessage: string },
  { state: RootState }
>(
  `${accountDeletionSliceName}/retry`,
  async (
    { successMessage, ...params },
    { dispatch, getState, rejectWithValue }
  ) => {
    const response = await services.retryDeletionRequest(params)
    if (!response.ok) {
      dispatch(errorSave(mapDeletionErrorDetails(response)))
      await dispatch(fetchDeletionRequests(getState().accountDeletion.filters))
      return rejectWithValue(
        response.data || { message: "Failed to retry deletion request" }
      )
    }

    showSuccess(successMessage)

    // Retry answers 202 the moment the task is queued, but anonymization runs
    // in a worker — refetching straight away just re-renders the same
    // `processing` row and reads as "nothing happened". Poll briefly until the
    // row leaves `processing`; if finalize is genuinely broken it stays put,
    // which is the honest outcome.
    for (let attempt = 0; attempt < RETRY_POLL_ATTEMPTS; attempt += 1) {
      await wait(RETRY_POLL_INTERVAL_MS)
      await dispatch(fetchDeletionRequests(getState().accountDeletion.filters))

      const stillProcessing = getState().accountDeletion.requests.some(
        (r) => r.id === params.id && r.status === "processing"
      )
      if (!stillProcessing) return
    }
  }
)
